import { auth } from '../sp-firebase/firebase';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

export interface ImportBatch {
  id: number;
  account_id: number;
  type: string;
  original_filename: string | null;
  status: string;
  review_status: string;
  approved_by_user_id: number | null;
  approved_at: string | null;
  review_comment: string | null;
  created_at: string;
  completed_at: string | null;
  error_summary: string | null;
  created_by_name: string | null;
  supply_count: number;
  demand_count: number;
  hedge_count: number;
  staging_count: number;
}

export interface SupplySummaryRow {
  formula_code: string;
  formula_name: string;
  supply_type: string;
  count: number;
  total_quantity: string;
  total_remaining: string;
  avg_price: string | null;
  price_currency: string | null;
}

export interface StagingIssue {
  parse_status: string;
  count: number;
}

/** Worker info returned by GET /api/v2/import/workers */
export interface ImportWorkerRole {
  role: string;
  filename: string;
  required: boolean;
  multiple?: boolean;
  maxFiles?: number;
}

export interface ImportWorkerInfo {
  name: string;
  label: string;
  roles: ImportWorkerRole[];
  expectedFiles?: { name: string; pattern: string; required: boolean }[];
}

/**
 * File analysis metadata — standardized across all workers.
 * Worker-specific sections (contracts, gl, mrp) are optional.
 * Shared sections (purchaseOrders, ratioPOs, inventory) use canonical field names.
 */
export interface FileAnalysis {
  workerType?: string; // 'strauss' | 'carmit' | 'strauss-mrp'
  // ── Strauss MRP (workerType 'strauss-mrp') ──
  monthsInFile?: string[];
  pastMonthsSkipped?: string[];
  materialsFound?: number;
  unmappedMaterials?: string[];
  byFormula?: { formulaCode: string; monthsCount: number; totalMT: number }[];
  materialsMap?: { sapCode: string; formulaCode: string; usedIn: string[] }[];
  // ── Strauss-only ──
  contracts?: {
    totalRows: number;
    parsed: number;
    skipped: number;
    uniqueContracts: number;
    deletedCount: number;
    unknownMaterialCount: number;
    byFormula: { formulaCode: string; count: number; targetQtyMT: number; openQtyMT: number }[];
  };
  // ── Shared: Purchase Orders ──
  purchaseOrders?: {
    totalRows: number;
    deletedCount: number;
    closedCount: number;
    openCount: number;
    noMaterialCount: number;
    overdueCount: number;
    supplyPositions: number;
    contractLookupHits: number;
    contractLookupMisses: number;
    byFormula: {
      formulaCode: string; count: number; totalQtyMT: number;
      remainingMT: number; weightedAvgPrice: number; currency: string;
    }[];
    lines: {
      poDoc: string; poItem: string; formulaCode: string;
      supplier: string; contractRef: string;
      poQtyMT: number; suppliedMT: number; remainingMT: number;
      netPrice: number; currency: string;
      deliveryDate: string; originalDeliveryDate: string; isOverdue: boolean; uom: string;
    }[];
  };
  // ── Shared: Ratio POs ──
  ratioPOs?: {
    count: number;
    totalQtyMT: number;
    lines: {
      contractId: string; contractItem: string; formulaCode: string;
      supplier: string; openQtyMT: number; ratio: number;
      currency: string; deliveryDate: string; uom: string;
    }[];
  };
  // ── Shared: Inventory ──
  inventory?: {
    totalRows: number;
    skippedRows: number;
    materialsFound: number;
    supplyPositions?: number;
    byFormula: {
      formulaCode: string; materialCode: string; description: string;
      unrestrictedKG: number; inTransitKG: number; qualityCheckKG: number;
      totalKG: number; totalMT: number; rowCount: number;
    }[];
  };
  // ── Strauss-only: GL Valuation ──
  gl?: {
    provided: boolean;
    skipped: boolean;
    totalRows: number;
    validEntries: number;
    valuations: {
      formulaCode: string; inventoryMT: number; coveredMT: number;
      avgPricePerMT: number; currency: string;
      avgPriceLocalPerMT: number; localCurrency: string;
      glEntriesUsed: number;
    }[];
  };
  // ── Carmit-only: MRP / Demand ──
  mrp?: {
    totalRows: number;
    nipukRows: number;
    skippedRows: number;
    aggregatedPeriods: number;
    demandPositions: number;
    byFormula: { formulaCode: string; months: number; totalMT: number; nipukRowsAggregated: number }[];
    periods: { formulaCode: string; monthKey: string; totalMT: number; rowCount: number }[];
  };
}

export interface ImportBatchMetadata {
  workerName?: string;
  durationSeconds?: number;
  inputDirectory?: string;
  filesProcessed?: { name: string }[];
  fileSummary?: {
    contracts?: { rows: number; parsed: number };
    purchaseOrders?: { rows: number; open: number; supplyPositions: number };
    ratioPOs?: { count: number };
    inventory?: { rows: number; matchedRows: number; materials: number; supplyPositions: number };
    gl?: { provided: boolean; entries: number; skipped: boolean };
    mrp?: { rows: number; nipukRows: number; demandPositions: number };
    supply?: { rows: number; parsed: number; errors: number };
    demand?: { rows: number; parsed: number; errors: number };
    hedges?: { rows: number; parsed: number; errors: number };
  };
  fileAnalysis?: FileAnalysis;
  totals?: {
    supplyPositions: number;
    demandPositions?: number;
    stagingRows: number;
    warnings: number;
    errors: number;
  };
  // Hedge-specific (ADMIS PDF worker)
  statementDate?: string;
  brokerAccount?: string;
  groupsParsed?: number;
  hedgePositionsCreated?: number;
  commodities?: string[];
  commodityMap?: Record<string, string>;
  warnings?: (string | { cat: string; row: number; msg: string })[];
  errors?: (string | { cat: string; row: number; msg: string })[];
  summary?: {
    commodity: string;
    month: string;
    lots: number;
    mt: number;
    avgPrice: number;
    closePrice: number;
    currency: string;
    side: string;
    formulaCode: string | null;
    isOption?: boolean;
    optionType?: 'call' | 'put';
    strikePrice?: number;
  }[];
}

export interface DemandSummaryRow {
  formula_code: string;
  formula_name: string;
  demand_kind: string;
  count: number;
  total_quantity: string;
  uom: string | null;
}

export interface HedgeSummaryRow {
  formula_code: string;
  formula_name: string;
  instrument_type: string;
  position_side: string;
  count: number;
  total_quantity: string;
  total_hedge_effect: string;
  currency: string | null;
}

export interface ImportBatchDetails extends ImportBatch {
  supplySummary: SupplySummaryRow[];
  demandSummary: DemandSummaryRow[];
  hedgeSummary: HedgeSummaryRow[];
  stagingIssues: StagingIssue[];
  metadata: ImportBatchMetadata | null;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function getImportBatches(accountId?: number, reviewStatus?: string): Promise<ImportBatch[]> {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  if (accountId) params.set('accountId', String(accountId));
  if (reviewStatus) params.set('reviewStatus', reviewStatus);

  const response = await fetch(`${API_URL}/api/v2/import/batches?${params}`, { headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to fetch batches (${response.status})`);
  }
  const data = await response.json();
  return data.batches;
}

export async function getImportBatchDetails(batchId: number): Promise<ImportBatchDetails> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/v2/import/batches/${batchId}`, { headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to fetch batch details (${response.status})`);
  }
  const data = await response.json();
  return data.batch;
}

export async function approveBatch(batchId: number, mode: 'replace' | 'add' = 'replace'): Promise<{ deletedBatches: number; deletedSupply: number; deletedDemand: number }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/v2/import/batches/${batchId}/approve`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ mode }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to approve batch (${response.status})`);
  }
  return response.json();
}

export async function rejectBatch(batchId: number, comment?: string): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/v2/import/batches/${batchId}/reject`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ comment }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to reject batch (${response.status})`);
  }
}

export async function deleteBatch(batchId: number): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/v2/import/batches/${batchId}`, {
    method: 'DELETE',
    headers,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to delete batch (${response.status})`);
  }
}

export async function purgeStagingRows(batchId: number): Promise<number> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/v2/import/batches/${batchId}/purge-staging`, {
    method: 'POST',
    headers,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to purge staging rows (${response.status})`);
  }
  const data = await response.json();
  return data.purged;
}

/** Role-tagged file map for the upload endpoint (generic — any role name) */
export type ImportFilesByRole = Record<string, File | File[]>;

export async function getImportWorkers(accountId?: number): Promise<ImportWorkerInfo[]> {
  const headers = await getAuthHeaders();
  const params = accountId ? `?accountId=${accountId}` : '';
  const response = await fetch(`${API_URL}/api/v2/import/workers${params}`, { headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to fetch workers (${response.status})`);
  }
  const data = await response.json();
  return data.workers;
}

export async function uploadImportFiles(filesByRole: ImportFilesByRole, accountId: number, workerName: string = 'strauss'): Promise<any> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  const formData = new FormData();
  formData.append('accountId', String(accountId));
  formData.append('workerName', workerName);
  for (const [role, fileOrFiles] of Object.entries(filesByRole)) {
    if (!fileOrFiles) continue;
    if (Array.isArray(fileOrFiles)) {
      for (const f of fileOrFiles) formData.append(role, f);
    } else {
      formData.append(role, fileOrFiles);
    }
  }

  const response = await fetch(`${API_URL}/api/v2/import/upload`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const msg = [error.error, error.details].filter(Boolean).join(': ') || `Upload failed (${response.status})`;
    throw new Error(msg);
  }
  return response.json();
}

/** Download the manual import XLSX template */
export async function downloadImportTemplate(accountId?: number): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  const params = accountId ? `?accountId=${accountId}` : '';
  const response = await fetch(`${API_URL}/api/v2/import/template${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(`Failed to download template (${response.status})`);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const filename = disposition?.match(/filename="([^"]+)"/)?.[1] || 'spotlock-import-template.xlsx';

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Comparison result from comparing a pending batch to active positions */
export interface BatchComparison {
  unchanged: number;
  changed: { poReference: string; formulaCode: string; changes: string[] }[];
  added: { poReference: string; formulaCode: string; quantity: number; delivery: string }[];
  removed: { poReference: string; formulaCode: string; quantity: number; delivery: string }[];
  summary: {
    totalCurrent: number;
    totalNew: number;
    byFormula: {
      formulaCode: string;
      currentQtyMT: number;
      newQtyMT: number;
      deltaMT: number;
      currentCount: number;
      newCount: number;
    }[];
  };
}

export async function compareBatch(batchId: number): Promise<BatchComparison> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/v2/import/batches/${batchId}/compare`, { headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to compare batch (${response.status})`);
  }
  const data = await response.json();
  return data.comparison;
}

export async function triggerImport(workerName: string, accountId: number, inputDirectory?: string): Promise<any> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/v2/import/trigger`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ workerName, accountId, inputDirectory }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to trigger import (${response.status})`);
  }
  return response.json();
}
